import React from 'react';
import Pressable from "react-native-web/dist/exports/Pressable";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import PropTypes from 'prop-types';
import { viewports } from '@telus-uds/system-constants';
import { StackView, Typography, useViewport, horizontalScrollUtils, useThemeTokensCallback } from '@telus-uds/components-base';
import styled from 'styled-components';
import { getTimestamp } from '../shared/VideoSplash/helpers';
import { VideoPropType, RefPropType } from './videoPropType';
import VideoSplash from '../shared/VideoSplash/VideoSplash';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  getItemPositionLayoutHandler,
  itemPositionsPropType
} = horizontalScrollUtils;

// Use a React Native (web) outer container so it can take an onLayout callback, to
// access position in VideoSlider's UDS HorizontalScroll and update its itemPositions
const createReactNativeStyles = _ref => {
  let {
    pressablePaddingBottom,
    pressablePaddingVertical,
    pressablePaddingHorizontal,
    pressableBorderTopWidth,
    pressableBorderTopColor
  } = _ref;
  return StyleSheet.create({
    container: {
      cursor: 'pointer'
    },
    horizontal: {
      paddingBottom: pressablePaddingBottom
    },
    framed: {
      paddingVertical: pressablePaddingVertical,
      paddingHorizontal: pressablePaddingHorizontal
    },
    framedLine: {
      borderTopWidth: pressableBorderTopWidth,
      borderTopColor: pressableBorderTopColor
    }
  });
};
const ImageContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "VideoPickerThumbnail__ImageContainer",
  componentId: "components-web__sc-1glxurq-0"
})(["padding:", ";border:", ";border-radius:", "px;"], props => `${props.outerBorderGap}px`, props => `${props.outerBorderWidth}px solid ${props.outerBorderColor}`, _ref2 => {
  let {
    outerBorderRadius
  } = _ref2;
  return outerBorderRadius;
});
const VideoThumbnail = /*#__PURE__*/styled.div.withConfig({
  displayName: "VideoPickerThumbnail__VideoThumbnail",
  componentId: "components-web__sc-1glxurq-1"
})(["position:relative;width:", ";flex-shrink:0;* button{display:none;}&::before{content:'';display:block;padding-bottom:56.25%;}&::after{content:'';border:", "px solid;border-color:", ";border-radius:", "px;position:absolute;top:0;left:0;right:0;bottom:0;}& > div{border-radius:", "px;}"], props => props.layout === 'vertical' ? '100%' : '144px', _ref3 => {
  let {
    borderWidth
  } = _ref3;
  return borderWidth;
}, _ref4 => {
  let {
    borderColor
  } = _ref4;
  return borderColor;
}, _ref5 => {
  let {
    borderRadius
  } = _ref5;
  return borderRadius;
}, _ref6 => {
  let {
    borderRadius
  } = _ref6;
  return borderRadius;
});
const ThumbnailTitleContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "VideoPickerThumbnail__ThumbnailTitleContainer",
  componentId: "components-web__sc-1glxurq-2"
})(["display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;"]);
const VideoPickerThumbnail = /*#__PURE__*/React.forwardRef((_ref7, ref) => {
  let {
    videoPlayerRef,
    selectedVideoId,
    video,
    onSelectVideo,
    layout = 'vertical',
    isFramed,
    itemPositions,
    index,
    width = '100%'
  } = _ref7;
  const viewport = useViewport();
  const getTokens = useThemeTokensCallback('VideoPickerThumbnail', {}, {});
  const {
    timestamp
  } = getTimestamp(video.videoLength, video.copy);
  const isPlaying = selectedVideoId === video.videoId;
  const renderThumbnailImage = themeTokens => {
    return /*#__PURE__*/_jsx(VideoThumbnail, {
      ...themeTokens,
      isPlaying: isPlaying,
      layout: layout,
      children: /*#__PURE__*/_jsx(VideoSplash, {
        simpleMode: true,
        poster: video.posterSrc || `https://img.youtube.com/vi/${video.videoId}/maxresdefault.jpg`,
        videoLength: video.videoLength,
        copy: video.copy
      })
    });
  };
  const renderThumbnailInfo = _ref8 => {
    let {
      titleColor,
      subTitleColor
    } = _ref8;
    return /*#__PURE__*/_jsxs(StackView, {
      space: 2,
      tokens: {
        flexShrink: 1
      },
      children: [/*#__PURE__*/_jsx(ThumbnailTitleContainer, {
        viewport: viewport,
        children: /*#__PURE__*/_jsx(Typography, {
          variant: {
            bold: true
          },
          tokens: {
            color: titleColor
          },
          children: video.title
        })
      }), viewport !== viewports.xs && /*#__PURE__*/_jsx(Typography, {
        variant: {
          size: 'micro'
        },
        tokens: {
          color: subTitleColor
        },
        children: timestamp
      })]
    });
  };
  const handleLayout = itemPositions !== undefined ? getItemPositionLayoutHandler(itemPositions.positions, index) : undefined;
  const onKeyPress = event => {
    if (['Space', 'Enter'].includes(event.key)) {
      onSelectVideo(video);
      const splashButton = videoPlayerRef.current?.querySelector('button');
      if (splashButton) splashButton.focus();
    }
  };
  return /*#__PURE__*/_jsx(Pressable, {
    onLayout: handleLayout,
    onPress: () => onSelectVideo(video),
    testID: `thumbnail-container-${video.videoId}`,
    onKeyPress: onKeyPress,
    accessibilityRole: "radio",
    accessibilityState: {
      checked: isPlaying
    },
    style: _ref9 => {
      let {
        hovered: hover,
        focused: focus,
        pressed
      } = _ref9;
      const themeTokens = getTokens({
        hover,
        focus,
        pressed,
        selected: isPlaying
      });
      const rnStyles = createReactNativeStyles(themeTokens);
      return [rnStyles.container, layout === 'horizontal' && rnStyles.horizontal, isFramed && rnStyles.framed, isFramed && index > 0 && rnStyles.framedLine, {
        width
      }, {
        outline: 'none'
      }];
    },
    ref: ref,
    children: _ref10 => {
      let {
        hovered: hover,
        focused: focus,
        pressed
      } = _ref10;
      const themeTokens = getTokens({
        hover,
        focus,
        pressed,
        selected: isPlaying
      });
      return /*#__PURE__*/_jsxs(StackView, {
        space: layout === 'vertical' ? 2 : 3,
        direction: layout === 'vertical' ? 'column' : 'row',
        children: [/*#__PURE__*/_jsx(ImageContainer, {
          ...themeTokens,
          children: renderThumbnailImage(themeTokens)
        }), renderThumbnailInfo(themeTokens)]
      });
    }
  }, video.videoId);
});
VideoPickerThumbnail.displayName = 'VideoPickerThumbnail';
VideoPickerThumbnail.propTypes = {
  selectedVideoId: PropTypes.string,
  onSelectVideo: PropTypes.func,
  video: VideoPropType,
  videoPlayerRef: RefPropType,
  layout: PropTypes.oneOf(['vertical', 'horizontal']),
  isFramed: PropTypes.bool,
  itemPositions: itemPositionsPropType,
  index: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};
export default VideoPickerThumbnail;